import { useCallback, useEffect, useState } from 'react';
import { useEventListener } from 'usehooks-ts';

export type ScreenPosition = 'left' | 'center' | 'right';

/**
 * Tracks which third of the viewport an element sits in, so a card that grows on hover can grow
 * away from the nearest edge instead of being clipped by it.
 *
 * The ref is a callback ref on purpose: cards are commonly rendered only once they scroll into
 * view, so a mount-only measurement would run while the node is still null and leave every card
 * on the initial position. Pass `recalculateOn` — a value that changes whenever the element moves
 * horizontally, e.g. a carousel's scroll counter — to have it measured again.
 */
export const useScreenPosition = (recalculateOn?: unknown) => {
  const [domElement, setDomElement] = useState<HTMLElement | null>(null);
  const [screenPosition, setScreenPosition] = useState<ScreenPosition>('left');

  const calculateScreenPosition = useCallback(() => {
    if (!domElement || !window) return;

    const screenWidth = window.innerWidth;
    const elementBox = domElement.getBoundingClientRect();

    if (!elementBox.width) return;
    if (elementBox.left > screenWidth) return setScreenPosition('right');

    const leftPercentage = ((elementBox.left + elementBox.width / 2) / screenWidth) * 100;
    if (leftPercentage < 33) {
      setScreenPosition('left');
    } else if (leftPercentage > 66) {
      setScreenPosition('right');
    } else {
      setScreenPosition('center');
    }
  }, [domElement]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measuring on `recalculateOn` is the point
  useEffect(() => {
    calculateScreenPosition();
  }, [calculateScreenPosition, recalculateOn]);

  useEventListener('resize', calculateScreenPosition);

  return { domElement: setDomElement, screenPosition, calculateScreenPosition };
};
